<script setup lang="ts">
import { isValidNumber } from 'libphonenumber-js'

import { UserPersonalData } from '@metras/frontend-shared/interfaces'

const emit = defineEmits(['update:closeModal'])
const currentStep = ref(1)
const amountOfSteps = 2

const toast = useToast()
const { mutate: mutateUser } = useModifyUser()
const personalSetupModel = ref({
  forename: '',
  surname: '',
  academicTitle: '',
  phoneNumber: '',
  faxNumber: ''
})
const closeDialog = () => {
  emit('update:closeModal', false)
}
const validateInputs = () => {
  if (currentStep.value === 1) {
    if (personalSetupModel.value.forename === '' || personalSetupModel.value.surname === '') {
      toast.error('Bitte füllen Sie die Pflichtfelder aus.')

      return false
    }
  }

  if (currentStep.value === amountOfSteps) {
    if (!isValidNumber(personalSetupModel.value.phoneNumber) && personalSetupModel.value.phoneNumber !== '') {
      toast.error('Telefonnummern müssen mit dem Ländercode beginnen, z.B. +43 für Österreich')

      return false
    }

    if (!isValidNumber(personalSetupModel.value.faxNumber) && personalSetupModel.value.faxNumber !== '') {
      toast.error('Faxnummern müssen mit dem Ländercode beginnen, z.B. +43 für Österreich')

      return false
    }
  }

  return true
}
const nextStep = () => {
  const isValid = validateInputs()

  if (!isValid) {
    return
  }

  if (currentStep.value < amountOfSteps) {
    currentStep.value++
  } else {
    const data: UserPersonalData = {
      forename: personalSetupModel.value.forename,
      surname: personalSetupModel.value.surname,
      academicTitle: personalSetupModel.value.academicTitle,
      phoneNumber: personalSetupModel.value.phoneNumber,
      faxNumber: personalSetupModel.value.faxNumber
    }

    mutateUser(data, {
      onSuccess: () => {
        toast.success('Ihre Daten wurden erfolgreich gespeichert.')
        personalSetupModel.value = {
          forename: '',
          surname: '',
          academicTitle: '',
          phoneNumber: '',
          faxNumber: ''
        }
        currentStep.value = 1
        closeDialog()
      },
      onError: () => {
        toast.error('Ihre Daten konnten nicht gespeichert werden.')
      }
    })
  }
}
const prevStep = () => {
  if (currentStep.value > 1) {
    currentStep.value--
  } else {
    closeDialog()
  }
}
const infoText = ['Sagen Sie uns Ihren Namen:', 'Sagen Sie uns Ihre Kontakdaten:']

const buttonEnabled = computed(() => {
  if (currentStep.value === 1) {
    return personalSetupModel.value.forename !== '' && personalSetupModel.value.surname !== ''
  }

  if (currentStep.value === 2) {
    return (
      isValidNumber(personalSetupModel.value.phoneNumber) ||
      personalSetupModel.value.phoneNumber === '' ||
      isValidNumber(personalSetupModel.value.faxNumber) ||
      personalSetupModel.value.faxNumber === ''
    )
  }
})
</script>

<template>
  <div class="bg-white flex justify-center items-center flex-col gap-10 p-8 w-full">
    <div class="flex justify-center items-center flex-col gap-2">
      <img src="../../assets//metras_logo.svg" alt="" class="aspect-square h-36 w-36">
      <span class="text-metras-red">Der einfache Weg zur Akkreditierung</span>
    </div>

    <div class="flex justify-start flex-col gap-3 w-full">
      <span class="font-medium text-2xl">Willkommen im METRAS-PORTAL!</span>
      <span class="text-xl">{{ infoText[currentStep - 1] }}</span>
    </div>
    <div class="flex justify-start w-full">
      <PersonalSetupNameForm v-if="currentStep === 1" v-model="personalSetupModel" />
      <PersonalSetupPhoneForm v-if="currentStep === 2" v-model="personalSetupModel" />
    </div>

    <div class="flex justify-between items-center flex-wrap gap-2 w-full">
      <ActionButton :icon="currentStep === 1 ? 'feather:x' : 'feather:chevrons-left'" :label="currentStep === 1 ? 'Abbrechen' : 'Zurück'" type="outlined" @click="prevStep" />
      <span class="self-center">Schritt {{ currentStep }} von {{ amountOfSteps }}</span>
      <ActionButton class="" icon="feather:chevrons-right" :label="currentStep < amountOfSteps ? 'Weiter' : 'Senden'" :disabled="!buttonEnabled" @click="nextStep" />
    </div>
  </div>
</template>
